'use client';

import { useCallback, useState, useRef, useEffect } from 'react';
import dynamic from 'next/dynamic';
import Icon, { IconId } from '@/components/shared/Icons';
import type { GenericTagProps } from '@/components/card/tags/Tag';
import { getSimpleClickFact } from '@/analytics/helpers/cventAnalytics/getSimpleClickFact';
import useMap from '@/hooks/useMap';
import useAnalytics from '@/hooks/useAnalytics';
import { convertToSnakeCase, VENUE_CARD_CLICK_TYPE } from '@/utils/analyticsClientHelper';
import { useBreakpoints } from '@/hooks/useBreakpoints';
import Button from '@/components/shared/button/Button';
import type { SimpleClickFact } from '@/analytics/models/cventAnalytics/SimpleClickFact';
import useScrollEvent from '@/hooks/useScrollEvent';

const Popper = dynamic(() => import('@/components/shared/popper/Popper'), { ssr: false });
const Modal = dynamic(() => import('@/components/shared/modal'), { ssr: false });

type PromotionProps = {
  closeString: string;
} & GenericTagProps;

// Represents the Promotion Tag for the venue cart
export default function PromotionTag({
  tag,
  venueDiamondType,
  venueId,
  searchResultsRank,
  searchTerm,
  closeString
}: PromotionProps): JSX.Element {
  const portalRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const { isMobile } = useBreakpoints();

  const { mapExpanded } = useMap();
  const analytics = useAnalytics();
  const triggerSimpleClickFact = useCallback(() => {
    const tagName = `venue_card_${convertToSnakeCase(tag.name)}`;
    const simpleClickFact = getSimpleClickFact(
      tagName,
      VENUE_CARD_CLICK_TYPE,
      searchResultsRank,
      venueId,
      mapExpanded,
      searchTerm,
      venueDiamondType
    );
    analytics.reportFact<SimpleClickFact>(simpleClickFact);
  }, [analytics, mapExpanded, venueId, searchResultsRank, searchTerm, tag, venueDiamondType]);

  const onDismiss = useCallback(() => {
    setIsOpen(false);
  }, []);

  const onTriggerEvent = useCallback(
    e => {
      e.preventDefault();
      e.stopPropagation();
      triggerSimpleClickFact();
      if (window !== undefined) {
        window.addEventListener('scroll', onDismiss, false);
      }
      setIsOpen(!isOpen);
    },
    [isOpen, onDismiss, triggerSimpleClickFact]
  );

  const handleClick = useCallback(e => {
    e.stopPropagation();
    setIsOpen(false);
  }, []);

  useScrollEvent(isOpen, onDismiss);

  useEffect(() => {
    if (!isOpen) {
      document.body.style.overflow = '';
    }
  }, [isOpen]);

  let promotionContent = <></>;
  if (isOpen) {
    promotionContent = isMobile ? (
      isOpen && (
        <Modal format="bottom" isOpen={isOpen} portal focusTrapRef={portalRef} onDismiss={onDismiss} zIndex={20}>
          <div className="max-h-[70vh] w-full overflow-y-auto rounded-lg bg-background p-5 text-b-xs/tight text-neutral-60 shadow-4">
            <div className="flex w-full justify-between">
              <h3 className="mb-2 text-b-base/tight font-medium uppercase">{tag.name}</h3>
              <Button
                type="button"
                onClick={handleClick}
                aria-label={closeString}
                icon={<Icon id="x" className="mb-2 h-6 w-6" />}
                appearance="ghost"
                className="justify-end rounded-none"
              />
            </div>
            {tag.content}
          </div>
        </Modal>
      )
    ) : (
      <Popper
        triggerRef={triggerRef}
        isOpen={isOpen}
        arrowSize="small"
        placement="top"
        zIndex={20}
        onDismiss={onDismiss}
      >
        <div className="w-80 rounded-lg bg-background pl-5 pt-5 text-b-xs/tight text-neutral-60 shadow-4">
          <h3 className="mb-2 text-b-base/tight font-medium uppercase">{tag.name}</h3>
          {tag.content}
        </div>
      </Popper>
    );
  }

  return (
    <div onMouseLeave={onDismiss} className="contents">
      {/* TODO: fix this */}
      {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
      <button
        data-cvent-id="promotion-tag"
        type="button"
        className="items-center py-0.5"
        onClick={onTriggerEvent}
        onMouseEnter={onTriggerEvent}
      >
        <div ref={triggerRef}>
          <Icon id={tag.icon as IconId} className="h-4 w-4" title={tag.name} />
        </div>
      </button>
      {promotionContent}
    </div>
  );
}
